import EventEmitter from 'events'
import { FC, ReactNode, createContext, useCallback, useContext, useEffect, useMemo } from 'react'
import StrictEventEmitter from 'strict-event-emitter-types'

interface Events {
  onVideoPlay: Event
  onVideoPause: Event
  onVolumeChange: Event
  onEnterPip: Event
  onLeavePip: Event
  onRateChange: Event
  onTimeUpdate: Event
  onLoadedData: Event
  onEnded: Event
  onCanPlay: Event
}

interface Context {
  eventEmitter: StrictEventEmitter<EventEmitter, Events>
  addEventListeners?: (videoElem: HTMLVideoElement) => void
}

export const VideoEventHandlerContext = createContext<Context>({
  eventEmitter: new EventEmitter(),
})

export const VideoEventHandlerProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const eventEmitter: StrictEventEmitter<EventEmitter, Events> = useMemo(
    () => new EventEmitter(),
    [],
  )

  const onVideoPlay = useCallback(
    (e: Event) => {
      eventEmitter.emit('onVideoPlay', e)
    },
    [eventEmitter],
  )
  const onVideoPause = useCallback(
    (e: Event) => {
      eventEmitter.emit('onVideoPause', e)
    },
    [eventEmitter],
  )
  const onVolumeChange = useCallback(
    (e: Event) => {
      eventEmitter.emit('onVolumeChange', e)
    },
    [eventEmitter],
  )
  const onEnterPip = useCallback(
    (e: Event) => {
      eventEmitter.emit('onEnterPip', e)
    },
    [eventEmitter],
  )
  const onLeavePip = useCallback(
    (e: Event) => {
      eventEmitter.emit('onLeavePip', e)
    },
    [eventEmitter],
  )
  const onRateChange = useCallback(
    (e: Event) => {
      eventEmitter.emit('onRateChange', e)
    },
    [eventEmitter],
  )
  const onTimeUpdate = useCallback(
    (e: Event) => {
      eventEmitter.emit('onTimeUpdate', e)
    },
    [eventEmitter],
  )
  const onLoadedData = useCallback(
    (e: Event) => {
      eventEmitter.emit('onLoadedData', e)
    },
    [eventEmitter],
  )
  const onEnded = useCallback(
    (e: Event) => {
      eventEmitter.emit('onEnded', e)
    },
    [eventEmitter],
  )
  const onCanPlay = useCallback(
    (e: Event) => {
      eventEmitter.emit('onCanPlay', e)
    },
    [eventEmitter],
  )

  const value: Context = useMemo(
    () => ({
      eventEmitter,
      addEventListeners(videoElem) {
        videoElem.removeEventListener('play', onVideoPlay)
        videoElem.removeEventListener('pause', onVideoPause)
        videoElem.removeEventListener('volumechange', onVolumeChange)
        videoElem.removeEventListener('enterpictureinpicture', onEnterPip)
        videoElem.removeEventListener('leavepictureinpicture', onLeavePip)
        videoElem.removeEventListener('ratechange', onRateChange)
        videoElem.removeEventListener('timeupdate', onTimeUpdate)
        videoElem.removeEventListener('loadeddata', onLoadedData)
        videoElem.removeEventListener('ended', onEnded)
        videoElem.removeEventListener('canplay', onCanPlay)

        videoElem.addEventListener('play', onVideoPlay)
        videoElem.addEventListener('pause', onVideoPause)
        videoElem.addEventListener('volumechange', onVolumeChange)
        videoElem.addEventListener('enterpictureinpicture', onEnterPip)
        videoElem.addEventListener('leavepictureinpicture', onLeavePip)
        videoElem.addEventListener('ratechange', onRateChange)
        videoElem.addEventListener('timeupdate', onTimeUpdate)
        videoElem.addEventListener('loadeddata', onLoadedData)
        videoElem.addEventListener('ended', onEnded)
        videoElem.addEventListener('canplay', onCanPlay)
      },
    }),
    [
      eventEmitter,
      onVideoPlay,
      onVideoPause,
      onVolumeChange,
      onEnterPip,
      onLeavePip,
      onRateChange,
      onTimeUpdate,
      onLoadedData,
      onEnded,
      onCanPlay,
    ],
  )
  return (
    <VideoEventHandlerContext.Provider value={value}>{children}</VideoEventHandlerContext.Provider>
  )
}

export const useVideoEventHandler = (handlers: {
  onVideoPlay?: (event: Event) => void
  onVideoPause?: (event: Event) => void
  onVolumeChange?: (event: Event) => void
  onEnterPip?: (event: Event) => void
  onLeavePip?: (event: Event) => void
  onRateChange?: (event: Event) => void
  onTimeUpdate?: (event: Event) => void
  onLoadedData?: (event: Event) => void
  onEnded?: (event: Event) => void
  onCanPlay?: (event: Event) => void
}) => {
  const value = useContext(VideoEventHandlerContext)

  useEffect(() => {
    const handler = handlers.onVideoPlay
    if (!!handler) {
      value.eventEmitter.on('onVideoPlay', handler)
      return () => {
        value.eventEmitter.off('onVideoPlay', handler)
      }
    }
  }, [value.eventEmitter, handlers.onVideoPlay])

  useEffect(() => {
    const handler = handlers.onVideoPause
    if (!!handler) {
      value.eventEmitter.on('onVideoPause', handler)
      return () => {
        value.eventEmitter.off('onVideoPause', handler)
      }
    }
  }, [value.eventEmitter, handlers.onVideoPause])

  useEffect(() => {
    const handler = handlers.onVolumeChange
    if (!!handler) {
      value.eventEmitter.on('onVolumeChange', handler)
      return () => {
        value.eventEmitter.off('onVolumeChange', handler)
      }
    }
  }, [value.eventEmitter, handlers.onVolumeChange])

  useEffect(() => {
    const handler = handlers.onEnterPip
    if (!!handler) {
      value.eventEmitter.on('onEnterPip', handler)
      return () => {
        value.eventEmitter.off('onEnterPip', handler)
      }
    }
  }, [value.eventEmitter, handlers.onEnterPip])

  useEffect(() => {
    const handler = handlers.onLeavePip
    if (!!handler) {
      value.eventEmitter.on('onLeavePip', handler)
      return () => {
        value.eventEmitter.off('onLeavePip', handler)
      }
    }
  }, [value.eventEmitter, handlers.onLeavePip])

  useEffect(() => {
    const handler = handlers.onRateChange
    if (!!handler) {
      value.eventEmitter.on('onRateChange', handler)
      return () => {
        value.eventEmitter.off('onRateChange', handler)
      }
    }
  }, [value.eventEmitter, handlers.onRateChange])

  useEffect(() => {
    const handler = handlers.onTimeUpdate
    if (!!handler) {
      value.eventEmitter.on('onTimeUpdate', handler)
      return () => {
        value.eventEmitter.off('onTimeUpdate', handler)
      }
    }
  }, [value.eventEmitter, handlers.onTimeUpdate])

  useEffect(() => {
    const handler = handlers.onLoadedData
    if (!!handler) {
      value.eventEmitter.on('onLoadedData', handler)
      return () => {
        value.eventEmitter.off('onLoadedData', handler)
      }
    }
  }, [value.eventEmitter, handlers.onLoadedData])

  useEffect(() => {
    const handler = handlers.onEnded
    if (!!handler) {
      value.eventEmitter.on('onEnded', handler)
      return () => {
        value.eventEmitter.off('onEnded', handler)
      }
    }
  }, [value.eventEmitter, handlers.onEnded])

  useEffect(() => {
    const handler = handlers.onCanPlay
    if (!!handler) {
      value.eventEmitter.on('onCanPlay', handler)
      return () => {
        value.eventEmitter.off('onCanPlay', handler)
      }
    }
  }, [value.eventEmitter, handlers.onCanPlay])

  return value
}
